/* eslint-disable no-magic-numbers */

import {
  DropdownButton,
  type DropdownMenuItemType,
  notifySuccess,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useCancelContractorInvoiceMutation,
  useGetContractorInvoice,
} from '@omnipresentgroup/domains';
import {
  type ContractorInvoice,
  ContractorInvoiceStateEnum,
} from '@omnipresentgroup/types';
import { useEffect, useMemo, useState } from 'react';

import { ContractorInvoiceCancellationModal } from './components/ContractorInvoiceCancellationModal';
import { Actions } from './types';

type Props = {
  actions: Actions[];
} & (
  | { invoiceId: string; invoice?: never }
  | { invoice: ContractorInvoice; invoiceId?: never }
);

export const ContractorInvoiceActions = ({
  invoiceId,
  invoice,
  actions,
}: Props) => {
  const { data: fetchedInvoice } = useGetContractorInvoice({
    id: invoiceId,
    enabled: Boolean(invoiceId) && !invoice,
  });

  const { state, invoiceRef } = invoice || fetchedInvoice || {};

  const { mutate: cancelInvoice, isSuccess: isCancelInvoiceSuccess } =
    useCancelContractorInvoiceMutation({ id: invoiceId || invoice?.id });

  const { modalProps } = useModal({});
  const [modalOpen, setModalOpen] = useState(false);

  const showCancel = state === ContractorInvoiceStateEnum.OPEN;

  useEffect(() => {
    if (isCancelInvoiceSuccess) {
      notifySuccess({
        title: `${invoiceRef} was cancelled`,
      });
    }
  }, [invoiceRef, isCancelInvoiceSuccess]);

  const dropdownOptions: DropdownMenuItemType[] = useMemo(() => {
    const options: DropdownMenuItemType[] = [];
    // eslint-disable-next-line no-loops/no-loops
    for (const action of actions) {
      switch (action) {
        case Actions.CANCEL: {
          if (showCancel) {
            options.push({
              name: 'Cancel invoice',
              onClick: () => setModalOpen(true),
            });
          }
          break;
        }
      }
    }
    return options;
  }, [actions, showCancel]);

  return invoiceRef && dropdownOptions.length > 0 ? (
    <>
      <DropdownButton icon="Dots" options={dropdownOptions} />
      <ContractorInvoiceCancellationModal
        modalProps={{
          ...modalProps,
          open: modalOpen,
          onOpenChange: setModalOpen,
        }}
        handleCancel={() => {
          setModalOpen(false);
        }}
        handleSubmit={() => {
          if (invoiceId || invoice?.id) {
            cancelInvoice();
            setModalOpen(false);
          }
        }}
        invoiceRef={invoiceRef}
      />
    </>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
};
